import { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  FileText,
  Download,
  BookOpen,
  Truck,
  ClipboardList,
  ExternalLink,
  Newspaper,
  Mail,
  Mic,
  Smartphone,
  Headphones,
  Sparkles,
  Menu,
  Ship,
  Package,
  Calculator,
  MessageSquare,
  Users,
  Shield,
  DollarSign,
  FolderOpen,
  Settings,
  Search,
  Eye,
  Lock,
  CheckCircle,
  Anchor,
  Leaf,
  Route,
  Building2,
  Wallet,
  Award,
  Target,
  Code,
  ArrowRight,
  ChevronRight,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";


// ============ PDF GUIDES ============
const downloadableGuides = [
  {
    id: "driver-gps-guide",
    title: "GPS Driver Tracking Instructions",
    description: "Complete guide for drivers using the myCargoCFS GPS tracking app - includes GDPR compliance and data protection information",
    filename: "GPS_Driver_Instructions.pdf",
    icon: MapPin,
    category: "Operations",
    pages: "2",
    audience: "Drivers & Partners",
    isNew: true,
  },
  {
    id: "visibility-brochure",
    title: "Total Shipment Visibility Brochure",
    description: "Marketing brochure showcasing our end-to-end tracking capabilities for ocean, air, rail, and road shipments",
    filename: "Total_Shipment_Visibility_Brochure.pdf",
    icon: Eye,
    category: "Marketing",
    pages: "3",
    audience: "Customers",
    isNew: true,
  },
  {
    id: "outlook-guide",
    title: "Outlook Add-in Guide",
    description: "Setup and usage guide for the myEuropacific Outlook integration - match emails to shipments, track containers, manage contacts",
    filename: "Outlook_Addin_User_Guide.pdf",
    icon: Mail,
    category: "Integrations",
    pages: "12+",
    audience: "Staff",
  },
  {
    id: "newsletter-guide",
    title: "Newsletter Module Guide",
    description: "Complete guide for managing industry news, email lists, and sending weekly newsletters",
    filename: "Newsletter_User_Guide.pdf",
    icon: Newspaper,
    category: "Marketing",
    pages: "10+",
    audience: "Staff",
  },
  {
    id: "tendering-guide",
    title: "Tendering System Guide",
    description: "Guide for creating and managing trucking tenders with smart suggestions",
    filename: "Tendering_User_Guide.pdf",
    icon: ClipboardList,
    category: "Operations",
    pages: "15+",
    audience: "Staff & Partners",
  },
  {
    id: "tendering-quick-ref",
    title: "Tendering Quick Reference",
    description: "Printable quick reference with step-by-step checklists",
    filename: "Tendering_Quick_Reference.pdf",
    icon: FileText,
    category: "Operations",
    pages: "2",
    audience: "Staff",
  },
];

// ============ GETTING STARTED GUIDES ============
const gettingStartedGuides = [
  {
    id: "welcome",
    title: "Welcome to myEuropacific",
    icon: BookOpen,
    category: "Overview",
    description: "Platform introduction and user types",
    sections: [
      {
        title: "About the Platform",
        content: `myEuropacific is a comprehensive logistics tracking platform built for Europacific d.o.o. It provides:

• **Real-time Container Tracking**: Track FCL and LCL shipments across 100+ carriers
• **Quote Management**: Request and manage shipping quotes
• **Transport Orders**: Create and monitor transport orders
• **AI-Powered Assistance**: Get help from Roket, our AI assistant`
      },
      {
        title: "User Types",
        content: `The platform supports two main user types:

• **Europacific Staff** (@europacific.com emails) - Full access to all features including Orders, Transport Orders, Pricing Module, and Admin functions
• **Customers** - Access to tracking, quote requests, and personal dashboard`
      },
      {
        title: "Two-Factor Authentication (2FA)",
        content: `For security, all users must complete email-based 2FA when logging in:

1. Enter your email and password
2. A 6-digit code will be sent to your email
3. Enter the code within 10 minutes to complete login
4. Use 'Resend Code' if you didn't receive it`
      },
    ]
  },
  {
    id: "fcl-tracking",
    title: "FCL Container Tracking",
    icon: Ship,
    category: "Tracking",
    description: "Track full container shipments in real-time",
    sections: [
      {
        title: "How to Track",
        content: `1. Go to **Tracking** in the main menu
2. Enter your container number (e.g., MSCU1234567)
3. Click **"Track"** or press Enter
4. View real-time status, location, and ETA`
      },
      {
        title: "Tracking Features",
        content: `• **100+ Carriers Supported**: Maersk, MSC, CMA CGM, and more
• **Real-time Updates**: Status updates as containers move
• **Document Access**: View shipping documents when available
• **Map View**: See container location on interactive map`
      },
      {
        title: "Adding to Watchlist",
        content: `Never miss an update by adding containers to your watchlist:

1. Track a container
2. Click the **star icon** or **"Add to Watchlist"**
3. Receive automatic email notifications for status changes
4. View all watched containers in **My Watchlist** tab`
      },
    ]
  },
  {
    id: "lcl-tracking",
    title: "LCL / BL Tracking",
    icon: Package,
    category: "Tracking",
    description: "Track less-than-container-load shipments",
    sections: [
      {
        title: "LCL Tracking",
        content: `LCL (Less than Container Load) shipments are tracked using the Bill of Lading (BL) number:

1. Go to **Tracking → LCL/BL** tab
2. Enter your House BL (HBL) or Master BL (MBL) number
3. View consolidation details and status`
      },
      {
        title: "Understanding LCL Status",
        content: `• **Booked**: Shipment confirmed
• **At Origin**: Cargo at departure port
• **In Transit**: On the water
• **At Destination**: Arrived at destination port
• **Delivered**: Final delivery completed`
      },
    ]
  },
  {
    id: "port-tracking",
    title: "Port Terminal Tracking",
    icon: Anchor,
    category: "Tracking",
    description: "Track containers at Koper and Rijeka ports",
    sections: [
      {
        title: "Supported Ports",
        content: `The Port Tracking module connects directly to terminal operating systems:

• **Port of Koper** (Slovenia) - Luka Koper d.d.
• **Port of Rijeka** (Croatia) - Jadranska Vrata`
      },
      {
        title: "Port Status Information",
        content: `• Gate-in/Gate-out timestamps
• Vessel loading/discharge status
• Customs clearance status
• Terminal location (block/row/tier)
• Container weight and seal numbers`
      },
    ]
  },
  {
    id: "quotes",
    title: "Quote Request System",
    icon: DollarSign,
    category: "Sales",
    description: "Request and manage shipping quotes",
    sections: [
      {
        title: "Requesting a Quote",
        content: `1. Go to **CRM → Quote System**
2. Fill in shipment details:
   - Origin and Destination
   - Cargo type and weight
   - Container type (20ft, 40ft, etc.)
3. Submit your request
4. Receive quotes from Europacific within 24 hours`
      },
      {
        title: "Managing Quotes",
        content: `• View all your quotes in **My Quotes Dashboard**
• Filter by status: Pending, Quoted, Accepted, Expired
• Accept quotes directly from the dashboard
• Download quote PDFs for your records`
      },
    ]
  },
  {
    id: "position-folders",
    title: "Orders / Position Folders",
    icon: FolderOpen,
    category: "Operations",
    isStaffOnly: true,
    description: "Manage shipment orders and documentation",
    sections: [
      {
        title: "Creating a Position Folder",
        content: `Position Folders (PFs) are the central hub for managing shipments:

1. Go to **Orders (PF)** in the sidebar
2. Click **"+ New PF"**
3. Fill in shipment details
4. Add containers, parties, and documents
5. Save and assign a PF number`
      },
      {
        title: "Key Features",
        content: `• **Document Management**: Upload and organize shipping docs
• **Status Tracking**: Automatic tracking updates
• **Party Management**: Link customers, agents, carriers
• **Financial Tracking**: Costs, revenues, and margins
• **Task Management**: Assign and track team tasks`
      },
    ]
  },
];

// ============ FEATURE GUIDES ============
const featureGuides = [
  {
    id: "linkedin-newsletter",
    title: "AI-Powered Newsletter System",
    icon: Newspaper,
    category: "Marketing",
    isNew: true,
    description: "Create engaging content for LinkedIn and email newsletters with AI assistance.",
    sections: [
      {
        title: "Overview",
        content: `The Newsletter System helps you create and distribute professional content across LinkedIn and email channels:

• **Dual Content Generation**: AI creates both short LinkedIn posts and long-form email newsletters
• **Image Suggestions**: Find relevant images from Unsplash
• **Subscriber Management**: Build and manage your email subscriber list
• **Multi-Channel Publishing**: Publish to LinkedIn, send emails, or both simultaneously`
      },
      {
        title: "Creating AI-Generated Content",
        content: `1. Navigate to **Admin → LinkedIn Newsletter**
2. Click **"+ Create New"** button
3. Enter your topic (e.g., "Latest shipping trends in 2026")
4. Select your preferred tone (Professional, Casual, Technical)
5. Click **"Generate Content"**
6. Review and edit both versions as needed
7. Click **"Find Images"** to search for relevant visuals`
      },
      {
        title: "Managing Subscribers",
        content: `**Adding Subscribers:**
1. Click the **"Subscribers"** tab
2. Click **"+ Add Subscriber"**
3. Enter email address
4. Click **"Add"**

**Importing Multiple:**
• Click **"Import CSV"** to bulk upload emails`
      },
      {
        title: "Publishing Content",
        content: `**To LinkedIn:**
1. Select your draft
2. Click **"Publish to LinkedIn"**

**To Email:**
1. Select your draft
2. Click **"Send Email"**
3. Choose recipients

**Both Channels:**
Click **"Publish All"** to post and send simultaneously`
      },
    ]
  },
  {
    id: "voice-notes",
    title: "Voice Note Transcription",
    icon: Mic,
    category: "Tools",
    isNew: true,
    description: "Record or upload voice notes and get instant English transcriptions.",
    sections: [
      {
        title: "Overview",
        content: `Capture meeting notes, client conversations, and ideas on the go:

• **Multi-Language Support**: Speak in any language - AI transcribes to English
• **Two Input Methods**: Record directly or upload audio files
• **Powered by OpenAI Whisper**: Industry-leading accuracy
• **Mobile-Friendly**: Optimized for smartphones`
      },
      {
        title: "Recording a Voice Note",
        content: `1. Navigate to **Tools → Voice Notes**
2. Click the **red microphone button** to start
3. Speak clearly
4. Click **Stop** when finished
5. Click **"Transcribe"** to convert to text
6. Save or copy the transcript`
      },
      {
        title: "Uploading Audio Files",
        content: `**Supported Formats:** MP3, WAV, M4A, WEBM, OGG

1. Click **"Upload Audio"** button
2. Select your file (max 25MB)
3. Wait for upload
4. Click **"Transcribe"**`
      },
    ]
  },
  {
    id: "mobile-preview",
    title: "Mobile App Preview",
    icon: Smartphone,
    category: "Admin",
    isNew: true,
    description: "Preview the dedicated mobile apps in a realistic phone simulator.",
    sections: [
      {
        title: "Overview",
        content: `Experience the Customer and Staff mobile apps from your desktop:

• **Customer App**: For clients to track shipments
• **Staff App**: For field staff and sales team
• **Realistic Simulator**: iPhone-style frame with swipe gestures`
      },
      {
        title: "Accessing the Simulator",
        content: `1. Go to **Admin → Mobile Simulator**
2. Select app type: Customer or Staff
3. Use mouse to interact with the phone
4. Switch between apps using the top tabs`
      },
    ]
  },
  {
    id: "support-queue",
    title: "Support Queue Management",
    icon: Headphones,
    category: "Support",
    isNew: true,
    description: "Handle live support chats with broadcast notifications and task assignment.",
    sections: [
      {
        title: "Overview",
        content: `Manage customer support requests efficiently:

• **Live Chat Integration**: Real-time customer conversations
• **Broadcast Notifications**: Alert all staff of new requests
• **Task Assignment**: Assign follow-ups to team members
• **Follow-Up Emails**: Automated and manual follow-ups`
      },
      {
        title: "Handling Support Requests",
        content: `1. View incoming requests in **Support Queue**
2. Click to open a chat
3. Respond to customer
4. Create tasks for follow-up if needed
5. Mark as resolved when complete`
      },
    ]
  },
  {
    id: "collapsible-sidebar",
    title: "Navigation Sidebar",
    icon: Menu,
    category: "Navigation",
    isNew: true,
    description: "Organize the sidebar with expandable/collapsible category groups.",
    sections: [
      {
        title: "Overview",
        content: `The sidebar organizes features into logical categories:

• **CRM**: Customer management and sales
• **Operations**: Orders, transport, tracking
• **Finance**: Invoices, payments, reports
• **Tools**: Utilities and calculators
• **Admin**: System settings and user management`
      },
      {
        title: "Expanding & Collapsing",
        content: `• Click category header to expand/collapse
• Expanded categories show all menu items
• Your expansion state is remembered
• Use search to quickly find features`
      },
    ]
  },
  {
    id: "lcl-api",
    title: "LCL Status API Integration",
    icon: Code,
    category: "Integrations",
    isNew: true,
    description: "Receive automated status updates from freight forwarder partners.",
    sections: [
      {
        title: "Overview",
        content: `The LCL Status API allows partners to push shipment updates directly:

• **Automatic Matching**: Updates linked to PFs via HBL numbers
• **Queue System**: Unmatched updates held for manual review
• **Real-time Processing**: Instant status updates in Position Folders`
      },
      {
        title: "Documentation",
        content: `Full API documentation available at:
**https://your-domain/docs/lcl-status**

Includes:
• Authentication (API keys)
• Endpoints and payloads
• Example requests
• Error handling`
      },
    ]
  },
];

export default function UnifiedGuidePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState("getting-started");

  const handleDownload = async (guide) => {
    setDownloading(guide.id);
    try {
      // Use the guides endpoint with the specific filename
      const downloadUrl = guide.filename 
        ? `${API_URL}/guides/${guide.filename}`
        : `${API_URL}/user-guide/download`;
      window.open(downloadUrl, "_blank");
      toast.success(`Opening ${guide.title}`);
    } catch (error) {
      toast.error("Failed to download guide");
    } finally {
      setDownloading(null);
    }
  };

  // Filter guides based on search
  const filterGuides = (guides) => {
    if (!searchQuery.trim()) return guides;
    const query = searchQuery.toLowerCase();
    return guides.filter(g => 
      g.title.toLowerCase().includes(query) ||
      g.description.toLowerCase().includes(query) ||
      g.category.toLowerCase().includes(query)
    );
  };

  const filteredGettingStarted = filterGuides(gettingStartedGuides);
  const filteredFeatures = filterGuides(featureGuides);
  const filteredPDFs = filterGuides(downloadableGuides);

  // Render content with markdown-like formatting
  const renderContent = (content) => {
    return content.split('\n').map((paragraph, idx) => {
      const formattedText = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      if (paragraph.trim().startsWith('•')) {
        return <p key={idx} className="my-1 ml-2" dangerouslySetInnerHTML={{ __html: formattedText }} />;
      }
      if (paragraph.trim().match(/^\d+\./)) {
        return <p key={idx} className="my-1 ml-2" dangerouslySetInnerHTML={{ __html: formattedText }} />;
      }
      if (paragraph.trim()) {
        return <p key={idx} className="my-2" dangerouslySetInnerHTML={{ __html: formattedText }} />;
      }
      return null;
    });
  };

  // Render guide card
  const GuideCard = ({ guide }) => {
    const Icon = guide.icon;
    return (
      <div className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-all relative overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
              guide.isNew ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              <Icon className={`h-6 w-6 ${guide.isNew ? 'text-blue-600' : 'text-slate-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg text-slate-900">{guide.title}</h3>
                {guide.isNew && (
                  <Badge className="bg-green-500 text-white text-xs">New</Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">{guide.description}</p>
              <Badge variant="outline" className="mt-2">{guide.category}</Badge>
            </div>
          </div>
        </div>
        
        {/* Sections Accordion */}
        <ScrollArea className="max-h-[350px]">
          <Accordion type="single" collapsible className="px-5 py-2">
            {guide.sections.map((section, idx) => (
              <AccordionItem key={idx} value={`section-${idx}`} className="border-b last:border-0">
                <AccordionTrigger className="py-3 hover:no-underline">
                  <span className="flex items-center gap-2 text-sm font-medium text-left">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">
                      {idx + 1}
                    </span>
                    {section.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="prose prose-sm prose-slate max-w-none pl-8 pb-2">
                    {renderContent(section.content)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollArea>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 py-8 pb-96">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-600 rounded-lg">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">User Guide</h1>
          </div>
          <p className="text-slate-600">Complete documentation for myEuropacific platform features and workflows</p>
        </div>

        {/* Search and Download */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="guide-search"
              placeholder="Search guides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => window.open(`${API_URL}/user-guide/download`, "_blank")}
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF Guide
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm p-1">
            <TabsTrigger value="getting-started" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <BookOpen className="h-4 w-4 mr-2" />
              Getting Started
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Sparkles className="h-4 w-4 mr-2" />
              Feature Guides
              <Badge className="ml-2 bg-green-500 text-white text-xs">New</Badge>
            </TabsTrigger>
            <TabsTrigger value="downloads" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Download className="h-4 w-4 mr-2" />
              Downloadable PDFs
            </TabsTrigger>
          </TabsList>

          {/* Getting Started */}
          <TabsContent value="getting-started" className="space-y-6">
            {filteredGettingStarted.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredGettingStarted.map((guide) => (
                  <GuideCard key={guide.id} guide={guide} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No results found</h3>
                <p className="text-slate-500">Try a different search term</p>
              </div>
            )}
          </TabsContent>

          {/* Feature Guides */}
          <TabsContent value="features" className="space-y-6">
            {filteredFeatures.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredFeatures.map((guide) => (
                  <GuideCard key={guide.id} guide={guide} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No results found</h3>
                <p className="text-slate-500">Try a different search term</p>
              </div>
            )}

            {/* Quick Access Links */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <ArrowRight className="h-5 w-5 text-blue-600" />
                Quick Access to Features
              </h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { href: "/linkedin-newsletter", icon: Newspaper, label: "Newsletter System" },
                  { href: "/voice-notes", icon: Mic, label: "Voice Notes" },
                  { href: "/mobile", icon: Smartphone, label: "Mobile Preview" },
                  { href: "/support-queue", icon: Headphones, label: "Support Queue" },
                ].map((link) => (
                  <a 
                    key={link.href}
                    href={link.href} 
                    className="flex items-center gap-2 p-3 rounded-lg border hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    <link.icon className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium">{link.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Downloadable PDFs */}
          <TabsContent value="downloads" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {filteredPDFs.map((guide) => {
                const Icon = guide.icon;
                return (
                  <div
                    key={guide.id}
                    className="bg-white rounded-xl p-6 border shadow-sm hover:shadow-md transition-shadow relative"
                  >
                    {guide.isNew && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">NEW</span>
                    )}
                    <div className="flex items-start gap-4">
                      <div className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        guide.isNew ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        <Icon className={`h-6 w-6 ${guide.isNew ? 'text-blue-600' : 'text-slate-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-slate-900 mb-1">
                          {guide.title}
                        </h3>
                        <p className="text-sm text-slate-600 mb-3">
                          {guide.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          <Badge variant="outline">{guide.category}</Badge>
                          <Badge variant="secondary">{guide.pages} pages</Badge>
                          <Badge variant="secondary">{guide.audience}</Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleDownload(guide)}
                            disabled={downloading === guide.id}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {downloading === guide.id ? "Opening..." : "Download"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/guides/${guide.filename}`, "_blank")}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredPDFs.length === 0 && (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No results found</h3>
                <p className="text-slate-500">Try a different search term</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Need a guide for a specific feature?</strong> Contact support and we'll create documentation for you.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
